import { Anchor, Badge, Table, Text } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { defaultCurrency } from "@/lib/currency";
import { formatDateRange } from "@/utils/formatDate";
import { formatCurrency } from "@/utils/formatEuro";

import type { ListTableRowProps } from "@/components/elements";
import type { Auction, AuctionColumns } from "@/types/auctions";
import type { Currency } from "@/types/currency";
import type { FC, ReactNode } from "react";

export type AuctionsTableRowProps = ListTableRowProps<Auction, AuctionColumns>;

export const AuctionsTableRow: FC<AuctionsTableRowProps> = ({ columns, data, to, ...props }) => {
  const { t } = useTranslation();
  const fontSize = { base: "xs", sm: "sm" };

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const renderCell = (column: AuctionColumns) => {
    try {
      switch (column) {
        case "name":
          return to ? (
            <Anchor
              component={Link}
              size="sm"
              fz={fontSize}
              to={to}
              onClick={(event) => event.stopPropagation()}
              lineClamp={2}
              title={data[column]}
            >
              {data[column]}
            </Anchor>
          ) : (
            <Text size="sm" fz={fontSize} lineClamp={2} title={data[column]}>
              {data[column]}
            </Text>
          );
        case "range_date":
          return (
            <Text component="span" fz={fontSize} style={{ whiteSpace: "nowrap" }}>
              {formatDateRange(data["start_date"], data["end_date"])}
            </Text>
          );
        case "place":
          return data[column] ? (
            <Badge variant="light-gray" p="md" display="inline-flex">
              {data[column]}
            </Badge>
          ) : null;
        case "total_value":
          return <>{formatCurrency(data.event_analysis?.total_value ?? 0, currency.code)}</>;
        case "number_of_lots":
          return (
            <>
              {/*  Important: the UI may display 1 even when the actual value is 0. Check "AuctionTable.tsx" for more details */}
              {data.event_analysis?.number_of_lots === 0 ? 1 : data.event_analysis?.number_of_lots}
            </>
          );
        case "num_cars":
          return (
            <Badge variant="light" size="sm" px="sm" py="2px" display="inline-flex">
              {t("components.Auctions.cars_on_sale", { value: data[column] || 0 })}
            </Badge>
          );
        default:
          return data[column] as ReactNode;
      }
    } catch (err) {
      console.error("Error:", err);
      return null;
    }
  };

  return (
    <Table.Tr style={{ cursor: to ? "pointer" : "default" }} {...props}>
      {columns.map((column) => (
        <Table.Td
          key={column}
          style={
            column === "name"
              ? {
                  minWidth: 250,
                }
              : {
                  width: "1%",
                  whiteSpace: "nowrap",
                }
          }
        >
          {renderCell(column)}
        </Table.Td>
      ))}
    </Table.Tr>
  );
};
